/**
 * audioEngine.ts — 纯 Web Audio 合成的 8-bit 音效引擎
 * 不加载任何外部 mp3/wav；全部用 OscillatorNode / GainNode 现场合成。
 *
 * 浏览器策略：AudioContext 必须在用户手势后 resume。
 * 默认静音；开启后 playBeep / playSelect / playAlert / playCoin 即可即时出声。
 */

export type SfxName =
  | "beep"
  | "select"
  | "alert"
  | "coin"
  /** 工单 7 游戏扩展：受击 / 技能 / 通关琶音 / 核心过载 */
  | "hit"
  | "skill"
  | "victory"
  | "over"
  /** 对话打字机 8-bit 滴滴声（像素游戏 NPC 说话）：中/低/高音三档 + 收尾高音 */
  | "blip"
  | "blip-lo"
  | "blip-hi"
  /** 关卡音乐工单：射击 / 换弹 / 点亮权限柱 / 过关跃迁 */
  | "shoot"
  | "reload"
  | "pillar"
  | "warp";

/** 滴滴声三档变体 */
export type BlipVariant = "blip" | "blip-lo" | "blip-hi";

/** 单发滴滴声合成参数（纯数据，供测试断言波形合法） */
export interface BlipToneParams {
  type: OscillatorType;
  /** 基频 Hz（含随机微抖后的实际值） */
  freq: number;
  endFreq: number;
  /** 时长 s——必须短促 */
  duration: number;
  /** 增益——必须非静音 */
  gain: number;
}

/**
 * 滴滴声基准参数表：短促方波 + 音高随机微抖（像素 NPC「嘟嘟嘟」）。
 * duration ≤ 0.06s、gain ≥ 0.1：防连点叠爆由调用方节流 + blipMinGapMs 兜底。
 */
export const BLIP_TUNING: Record<
  BlipVariant,
  { type: OscillatorType; freq: number; jitterRatio: number; duration: number; gain: number }
> = {
  blip: { type: "square", freq: 620, jitterRatio: 0.09, duration: 0.035, gain: 0.14 },
  "blip-lo": { type: "square", freq: 392, jitterRatio: 0.07, duration: 0.05, gain: 0.12 },
  "blip-hi": { type: "square", freq: 920, jitterRatio: 0.1, duration: 0.045, gain: 0.15 },
};

/**
 * 纯函数：算出一发滴滴声的最终合成参数。
 * rand 注入便于测试确定性；默认 Math.random 微抖 ±jitterRatio。
 */
export function blipToneParams(
  variant: BlipVariant,
  rand: () => number = Math.random,
): BlipToneParams {
  const t = BLIP_TUNING[variant];
  const jitter = (rand() * 2 - 1) * t.jitterRatio;
  const freq = Math.max(80, t.freq * (1 + jitter));
  return {
    type: t.type,
    freq,
    endFreq: freq * 1.06,
    duration: t.duration,
    gain: t.gain,
  };
}

type Wave = OscillatorType;

export interface AudioEngineStatus {
  enabled: boolean;
  unlocked: boolean;
  volume: number;
}

/**
 * 懒加载 AudioContext 单例 —— 禁止模块顶层创建。
 * SSR / 无 window 环境返回 null，调用方必须前置校验。
 */
let audioCtx: AudioContext | null = null;
export const getAudioContext = (): AudioContext | null => {
  if (typeof window === "undefined") return null;
  if (!audioCtx) {
    const AudioCtxClass =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!AudioCtxClass) return null;
    try {
      audioCtx = new AudioCtxClass();
    } catch {
      return null;
    }
  }
  return audioCtx;
};

class AudioEngine {
  private master: GainNode | null = null;
  private enabled = false;
  private unlocked = false;
  private volume = 0.35;
  /** 滴滴声防叠爆：上次触发时刻（ms）与最小间隔 */
  private lastBlipAtMs: number | null = null;
  private blipMinGapMs = 24;

  private get ctx(): AudioContext | null {
    return getAudioContext();
  }

  /** 当前状态（供 HUD 显示） */
  status(): AudioEngineStatus {
    return {
      enabled: this.enabled,
      unlocked: this.unlocked,
      volume: this.volume,
    };
  }

  /** 用户手势里调用：解锁 AudioContext（必须）。SSR/无 window 安全。 */
  async unlock(): Promise<void> {
    try {
      const ctx = getAudioContext();
      if (!ctx) return;
      if (!this.master) {
        this.master = ctx.createGain();
        this.master.gain.value = this.enabled ? this.volume : 0;
        this.master.connect(ctx.destination);
      }
      if (ctx.state === "suspended") {
        await ctx.resume();
      }
      this.unlocked = ctx.state === "running";
    } catch {
      // 音频子系统不可用时静默降级，绝不影响 UI
    }
  }

  /** 状态栏声音总开关 */
  setEnabled(on: boolean): void {
    this.enabled = on;
    try {
      const ctx = getAudioContext();
      if (this.master && ctx) {
        this.master.gain.setTargetAtTime(
          on ? this.volume : 0,
          ctx.currentTime,
          0.01,
        );
      }
    } catch {
      /* 降级 */
    }
    if (on) void this.unlock();
  }

  toggle(): boolean {
    this.setEnabled(!this.enabled);
    return this.enabled;
  }

  setVolume(v: number): void {
    this.volume = Math.min(1, Math.max(0, v));
    try {
      const ctx = getAudioContext();
      if (this.master && ctx && this.enabled) {
        this.master.gain.setTargetAtTime(this.volume, ctx.currentTime, 0.01);
      }
    } catch {
      /* 降级 */
    }
  }

  /** 播放命名音效 */
  play(name: SfxName): void {
    if (!this.enabled) return;
    try {
      void this.unlock().then(() => {
        switch (name) {
          case "beep":
            this.playBeep();
            break;
          case "select":
            this.playSelect();
            break;
          case "alert":
            this.playAlert();
            break;
          case "coin":
            this.playCoin();
            break;
          case "hit":
            this.playHit();
            break;
          case "skill":
            this.playSkill();
            break;
          case "victory":
            this.playVictory();
            break;
          case "over":
            this.playOver();
            break;
          case "blip":
          case "blip-lo":
          case "blip-hi":
            this.playBlip(name);
            break;
          case "shoot":
            this.playShoot();
            break;
          case "reload":
            this.playReload();
            break;
          case "pillar":
            this.playPillar();
            break;
          case "warp":
            this.playWarp();
            break;
        }
      });
    } catch {
      /* 降级：音频失败不影响交互 */
    }
  }

  /**
   * 方波/三角波短促高频 —— 按钮悬停 / 打字机出字
   */
  playBeep(): void {
    const t = this.now();
    this.tone({
      type: "square",
      freq: 880,
      endFreq: 990,
      start: t,
      duration: 0.06,
      gain: 0.22,
    });
    this.tone({
      type: "triangle",
      freq: 1760,
      start: t,
      duration: 0.04,
      gain: 0.08,
    });
  }

  /**
   * 上升琶音 —— 确认 / 点击
   * C5 → E5 → G5 → C6
   */
  playSelect(): void {
    const t = this.now();
    const notes = [523.25, 659.25, 783.99, 1046.5];
    notes.forEach((freq, i) => {
      this.tone({
        type: "square",
        freq,
        start: t + i * 0.055,
        duration: 0.07,
        gain: 0.2,
      });
    });
  }

  /**
   * 双脉冲下坠警告 —— 高危权限拦截
   */
  playAlert(): void {
    const t = this.now();
    // 脉冲 1
    this.tone({
      type: "sawtooth",
      freq: 660,
      endFreq: 220,
      start: t,
      duration: 0.14,
      gain: 0.22,
    });
    // 脉冲 2
    this.tone({
      type: "sawtooth",
      freq: 550,
      endFreq: 165,
      start: t + 0.16,
      duration: 0.16,
      gain: 0.22,
    });
  }

  /**
   * 清脆双频金币声 —— 钱包交互 / 复制邮箱
   * B5 → E6（经典 coin 间隔）
   */
  playCoin(): void {
    const t = this.now();
    this.tone({
      type: "square",
      freq: 987.77,
      start: t,
      duration: 0.07,
      gain: 0.18,
    });
    this.tone({
      type: "square",
      freq: 1318.51,
      start: t + 0.07,
      duration: 0.18,
      gain: 0.2,
    });
  }

  /**
   * 受击噪声短坠 —— 伴侣扣血 / 碰撞
   */
  playHit(): void {
    const t = this.now();
    this.tone({
      type: "sawtooth",
      freq: 320,
      endFreq: 90,
      start: t,
      duration: 0.12,
      gain: 0.26,
    });
    this.tone({
      type: "square",
      freq: 140,
      endFreq: 60,
      start: t + 0.02,
      duration: 0.1,
      gain: 0.16,
    });
  }

  /**
   * 战技上扫 —— 技能释放 / 代码脉冲
   */
  playSkill(): void {
    const t = this.now();
    this.tone({
      type: "square",
      freq: 220,
      endFreq: 1320,
      start: t,
      duration: 0.12,
      gain: 0.18,
    });
    this.tone({
      type: "triangle",
      freq: 440,
      endFreq: 1760,
      start: t + 0.04,
      duration: 0.1,
      gain: 0.1,
    });
  }

  /**
   * 通关 8-bit 上升胜利琶音（工单 7）—— C5→E5→G5→C6→E6→G6→C7 级联
   */
  playVictory(): void {
    const t = this.now();
    const notes = [523.25, 659.25, 783.99, 1046.5, 1318.51, 1567.98, 2093.0];
    notes.forEach((freq, i) => {
      this.tone({
        type: "square",
        freq,
        start: t + i * 0.09,
        duration: i === notes.length - 1 ? 0.35 : 0.1,
        gain: 0.22,
      });
      this.tone({
        type: "triangle",
        freq: freq * 2,
        start: t + i * 0.09,
        duration: 0.06,
        gain: 0.07,
      });
    });
  }

  /**
   * 核心转储 —— HP 归零 / 终极过载警示
   */
  playOver(): void {
    const t = this.now();
    const notes = [392.0, 349.23, 293.66, 220.0, 164.81];
    notes.forEach((freq, i) => {
      this.tone({
        type: "sawtooth",
        freq,
        endFreq: freq * 0.82,
        start: t + i * 0.12,
        duration: 0.14,
        gain: 0.2,
      });
    });
  }

  /**
   * 对话打字机 8-bit 滴滴声 —— 短促方波 + 音高随机微抖（像素 NPC「嘟嘟嘟」）。
   * blipMinGapMs 内重复触发直接吞掉：打字机逐字 + 快速连点都不叠爆。
   */
  playBlip(variant: BlipVariant): void {
    const gapMs = this.blipMinGapMs;
    if (gapMs > 0) {
      const nowMs = Date.now();
      if (this.lastBlipAtMs != null && nowMs - this.lastBlipAtMs < gapMs) return;
      this.lastBlipAtMs = nowMs;
    }
    const p = blipToneParams(variant);
    const t = this.now();
    this.tone({
      type: p.type,
      freq: p.freq,
      endFreq: p.endFreq,
      start: t,
      duration: p.duration,
      gain: p.gain,
    });
  }

  /** 设置滴滴声最小间隔（ms）；0 关闭防叠爆。测试可读写。 */
  setBlipMinGapMs(ms: number): void {
    this.blipMinGapMs = Math.max(0, ms);
  }

  /**
   * shoot — short noise-like square burst (8-bit muzzle)
   */
  playShoot(): void {
    const t = this.now();
    this.tone({
      type: "square",
      freq: 720,
      endFreq: 180,
      start: t,
      duration: 0.055,
      gain: 0.2,
    });
    this.tone({
      type: "sawtooth",
      freq: 240,
      endFreq: 90,
      start: t,
      duration: 0.04,
      gain: 0.12,
    });
  }

  /**
   * reload — two-stage mechanical click + chamber rise
   */
  playReload(): void {
    const t = this.now();
    this.tone({
      type: "square",
      freq: 160,
      endFreq: 120,
      start: t,
      duration: 0.045,
      gain: 0.14,
    });
    this.tone({
      type: "square",
      freq: 280,
      endFreq: 420,
      start: t + 0.06,
      duration: 0.07,
      gain: 0.16,
    });
    this.tone({
      type: "triangle",
      freq: 880,
      start: t + 0.12,
      duration: 0.05,
      gain: 0.1,
    });
  }

  /**
   * pillar — rising grant arpeggio (ACCESS GRANTED)
   */
  playPillar(): void {
    const t = this.now();
    const notes = [523.25, 659.25, 1046.5];
    notes.forEach((freq, i) => {
      this.tone({
        type: "square",
        freq,
        start: t + i * 0.06,
        duration: 0.1,
        gain: 0.2,
      });
      this.tone({
        type: "triangle",
        freq: freq * 2,
        start: t + i * 0.06,
        duration: 0.05,
        gain: 0.07,
      });
    });
  }

  /**
   * warp — raster-expand sweep 160 to 1600 Hz plus tail sparkle
   */
  playWarp(): void {
    const t = this.now();
    this.tone({
      type: "sawtooth",
      freq: 160,
      endFreq: 1600,
      start: t,
      duration: 0.38,
      gain: 0.18,
    });
    this.tone({
      type: "square",
      freq: 320,
      endFreq: 1800,
      start: t + 0.08,
      duration: 0.3,
      gain: 0.1,
    });
    this.tone({
      type: "triangle",
      freq: 2093,
      start: t + 0.36,
      duration: 0.2,
      gain: 0.16,
    });
  }


  // ── 内部合成原语 ──────────────────────────────────────────

  private now(): number {
    return this.ctx ? this.ctx.currentTime : 0;
  }

  private tone(opts: {
    type: Wave;
    freq: number;
    endFreq?: number;
    start: number;
    duration: number;
    gain: number;
  }): void {
    if (!this.ctx || !this.master) return;
    const { ctx, master } = this;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();

    osc.type = opts.type;
    osc.frequency.setValueAtTime(opts.freq, opts.start);
    if (opts.endFreq && opts.endFreq !== opts.freq) {
      osc.frequency.exponentialRampToValueAtTime(
        opts.endFreq,
        opts.start + opts.duration,
      );
    }

    // 硬起音 + 快速衰减 → 像素打击感
    g.gain.setValueAtTime(0, opts.start);
    g.gain.linearRampToValueAtTime(opts.gain, opts.start + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, opts.start + opts.duration);

    osc.connect(g);
    g.connect(master);
    osc.start(opts.start);
    osc.stop(opts.start + opts.duration + 0.02);
  }
}

/** 全局单例 */
export const audioEngine = new AudioEngine();

/** React Hook 风格的便捷绑定（可选） */
export function bindHoverBeep(el: HTMLElement | null): () => void {
  if (!el) return () => {};
  const onEnter = () => audioEngine.play("beep");
  el.addEventListener("mouseenter", onEnter);
  return () => el.removeEventListener("mouseenter", onEnter);
}
